import { useEffect, useRef, useState } from 'react'
import { useApp } from '@/core/state/appStore'
import { LoadingState, ScrollBar, toastError } from 'admination-react'
import { currentVariant } from '@/core/data/session'
import { getOrganisation, updateOrganisation, type Organisation, type OrganisationPatch } from '@/core/data/organisation'
import { listStatus, type StatusOption } from '@/core/data/status'
import { listCoachVisibleStatusIds, setCoachStatusVisible } from '@/core/data/coachStatusSicht'
import { getEcnStandardUe, updateEcnStandardUe } from '@/variants/azav/data/organisationEcnSettings'
import { OrgStempelSection } from './SigningAssetsSection'
import { AmpelSettingsSection } from './AmpelSettingsSection'
import { InformationsblattSection } from './InformationsblattSection'
import { descriptionFor } from '@/variants'
import type { Stage } from '@/core/types'
import { TextField } from '@/core/components/form/TextField'
import { Checkbox } from '@/core/components/form/Checkbox'
import { SelectField } from '@/core/components/form/Select'
import { friendlyErrorMessage } from '@/core/lib/dbError'
import { captureWriteIdentity, isWriteIdentityCurrent } from '@/core/api/http'
import { TestdatenSection } from './TestdatenSection'

const ORG_SECTIONS = [
  ['Stammdaten', [
    ['name', 'Name'],
    ['telefonnummer', 'Telefonnummer'],
    ['kontakt_email', 'Kontakt-E-Mail'],
  ]],
  ['Adresse', [
    ['strasse', 'Straße'],
    ['hausnummer', 'Hausnummer'],
    ['plz', 'PLZ'],
    ['ort', 'Ort'],
  ]],
  ['Durchführungsort', [
    ['durchf_strasse', 'Straße'],
    ['durchf_hausnummer', 'Hausnummer'],
    ['durchf_plz', 'PLZ'],
    ['durchf_ort', 'Ort'],
  ]],
] as const

type OrgKey = typeof ORG_SECTIONS[number][1][number][0]
type OrgForm = Record<OrgKey, string>

function toForm(org: Organisation): OrgForm {
  return Object.fromEntries(ORG_SECTIONS.flatMap(([, fields]) =>
    fields.map(([key]) => [key, org[key] ?? '']),
  )) as OrgForm
}

function toPatchValue(key: keyof OrgForm, raw: string): string | null {
  return key === 'name' || key === 'kontakt_email'
    ? raw.trim()
    : raw.trim() === ''
      ? null
      : raw
}

function CoachVisibilitySection() {

  const { columns: COLUMNS, stageForStatusName } = descriptionFor(currentVariant())
  const [statusByStage, setStatusByStage] = useState<Partial<Record<Stage, StatusOption>>>({})
  const [visibleIds, setVisibleIds] = useState<Set<string> | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([listStatus(), listCoachVisibleStatusIds()])
      .then(([statuses, ids]) => {
        if (cancelled) return
        const byStage: Partial<Record<Stage, StatusOption>> = {}
        for (const s of statuses) {
          const stage = stageForStatusName(s.name)
          if (stage) byStage[stage] = s
        }
        setStatusByStage(byStage)
        setVisibleIds(ids)
      })
      .catch((e) => {
        if (cancelled) return
        console.error('Coach-Sichtbarkeit laden fehlgeschlagen:', e)
        setError('Coach-Sichtbarkeit konnte nicht geladen werden.')
      })
    return () => {
      cancelled = true
    }
  }, [stageForStatusName])

  const isStageVisible = (stage: Stage) => {
    const status = statusByStage[stage]
    return !!visibleIds && !!status && visibleIds.has(status.id)
  }

  async function toggle(stage: Stage, next: boolean) {
    if (!visibleIds) return
    const status = statusByStage[stage]
    if (!status) {

      toastError(
        `Status "${stage}" ist in der Datenbank noch nicht angelegt (Migration evtl. noch nicht angewendet?).`,
      )
      return
    }
    const identity = captureWriteIdentity()
    setVisibleIds((v) => {
      const ids = new Set(v)
      if (next) ids.add(status.id)
      else ids.delete(status.id)
      return ids
    })
    try {
      await setCoachStatusVisible(status.id, next)
      return
    } catch (e) {
      if (!isWriteIdentityCurrent(identity)) return
      console.error('Coach-Sichtbarkeit speichern fehlgeschlagen:', e)
      toastError(friendlyErrorMessage(e, 'Speichern fehlgeschlagen.'))
    }
    try {
      const ids = await listCoachVisibleStatusIds()
      if (isWriteIdentityCurrent(identity)) setVisibleIds(ids)
    } catch (e) {
      if (!isWriteIdentityCurrent(identity)) return
      console.error('Coach-Sichtbarkeit neu laden fehlgeschlagen:', e)
      setError('Coach-Sichtbarkeit konnte nicht geladen werden.')
    }
  }

  return (
    <div className="section">
      <h3 className="section-header">Coach-Sichtbarkeit</h3>
      <div className="section-body">
        {visibleIds === null ? (
          <LoadingState>Die Sichtbarkeit wird geladen …</LoadingState>
        ) : (
          COLUMNS.map((col) => (
            <Checkbox
              key={col.id}
              label={col.dbName}
              checked={isStageVisible(col.id)}
              onChange={(v) => void toggle(col.id, v)}
            />
          ))
        )}
        {error && <p className="field-error">{error}</p>}
      </div>
    </div>
  )
}

function EcnSettingsSection({
  organisationId,
}: {
  organisationId: string
}) {
  const [ueDefault, setUeDefault] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    getEcnStandardUe(organisationId)
      .then((ue) => {
        if (!cancelled) setUeDefault(ue)
      })
      .catch((e) => {
        console.error('Coachingnachweis-Einstellungen laden fehlgeschlagen:', e)
        if (!cancelled) setError('Coachingnachweis-Einstellungen konnten nicht geladen werden.')
      })
    return () => {
      cancelled = true
    }
  }, [organisationId])

  async function changeUe(next: string) {
    const n = Number(next)
    if (!Number.isFinite(n)) return
    const identity = captureWriteIdentity()
    const prev = ueDefault
    setUeDefault(n)
    try {
      await updateEcnStandardUe(organisationId, n)
    } catch (e) {
      if (!isWriteIdentityCurrent(identity)) return
      setUeDefault(prev)
      console.error('ecn_standard_ue speichern fehlgeschlagen:', e)
      toastError(friendlyErrorMessage(e, 'Speichern fehlgeschlagen.'))
    }
  }

  return (
    <div className="section">
      <h3 className="section-header">Coachingnachweis</h3>
      <div className="section-body">
        {ueDefault === null ? (
          <LoadingState>Die Einstellung wird geladen …</LoadingState>
        ) : (

          <SelectField
            label="Standard-UE je Sitzung"
            options={['1', '2', '3', '4']}
            value={String(ueDefault)}
            onChange={(v) => void changeUe(v)}
            required
          />
        )}
        {error && <p className="field-error">{error}</p>}
      </div>
    </div>
  )
}

export function OrgSettings() {
  const scrollerRef = useRef<HTMLElement>(null)
  const { identity, state } = useApp()
  const isOwner = identity?.isOwner ?? false
  const organisationId = identity?.organisationId
  const [form, setForm] = useState<OrgForm | null>(null)

  const formRef = useRef<OrgForm | null>(null)
  formRef.current = form
  const lastSaved = useRef<OrgForm | null>(null)

  useEffect(() => {
    if (!organisationId) return
    let cancelled = false
    getOrganisation(organisationId)
      .then((org) => {
        if (cancelled || !org) return
        const f = toForm(org)
        setForm(f)
        lastSaved.current = f
      })
      .catch((e) => {
        if (cancelled) return
        console.error('Organisationsdaten laden fehlgeschlagen:', e)
        toastError('Organisationsdaten konnten nicht geladen werden.')
      })
    return () => {
      cancelled = true
    }
  }, [organisationId])

  const set = (key: keyof OrgForm) => (v: string) => setForm((f) => (f ? { ...f, [key]: v } : f))

  const commit = (key: keyof OrgForm) => () => {
    const cur = formRef.current
    if (!cur || !organisationId || !isOwner) return
    const raw = cur[key]
    if (lastSaved.current && lastSaved.current[key] === raw) return
    const identity = captureWriteIdentity()
    void updateOrganisation(organisationId, { [key]: toPatchValue(key, raw) } as OrganisationPatch)
      .then(() => {
        if (!isWriteIdentityCurrent(identity)) return
        if (lastSaved.current) lastSaved.current = { ...lastSaved.current, [key]: raw }
      })
      .catch((e) => {
        if (!isWriteIdentityCurrent(identity)) return
        console.error(`Organisationsdaten (${key}) speichern fehlgeschlagen:`, e)
        toastError(friendlyErrorMessage(e, 'Speichern fehlgeschlagen.'))
      })
  }

  const field = (key: OrgKey, label: string) => (
    <div key={key} onBlur={commit(key)} onKeyDown={(event) => {
      if (event.key === 'Enter') (event.target as HTMLElement).blur()
    }}>
      <TextField label={label} value={form?.[key] ?? ''} onChange={set(key)} readOnly={!isOwner || form === null} />
    </div>
  )

  return (
    <>
    <main className="form" data-scroll-host ref={scrollerRef} inert={state.workspaceSwitching}>
      <div className="form-column">
        {ORG_SECTIONS.map(([title, fields]) => (
          <div className="section" key={title}>
            <h3 className="section-header">{title}</h3>
            <div className="section-body">{fields.map(([key, label]) => field(key, label))}</div>
          </div>
        ))}

        {isOwner && <CoachVisibilitySection />}

        {isOwner && organisationId && <AmpelSettingsSection organisationId={organisationId} />}

        {isOwner && organisationId && descriptionFor(currentVariant()).hasEcnSettings && (
          <EcnSettingsSection organisationId={organisationId} />
        )}

        {isOwner && organisationId && <OrgStempelSection organisationId={organisationId} />}
        {isOwner && organisationId && currentVariant() === 'azav' && <InformationsblattSection />}
        {isOwner && <TestdatenSection />}

      </div>
    </main>
    <ScrollBar scrollerRef={scrollerRef} />
    </>
  )
}
